import { FormLabel, FormGroup, FormControl } from '@mui/material';
import { FieldValues, Control, Controller, Path } from 'react-hook-form';
import { getError } from '../utils.ts';
import { Select, SelectProps } from '../../Select/index.ts';
import { ReactNode } from 'react';

interface ExtendedProps<T extends FieldValues> {
  // Control prop from the react-hook-form's 'useForm'
  name: Path<T>;
  control: Control<T>;
  header?: string;
  className?: string;
}

export type HookFormSelectProps<T extends FieldValues> = ExtendedProps<T> &
  SelectProps;

const HookFormSelect = <T extends FieldValues>(
  props: HookFormSelectProps<T>,
): ReactNode => {
  const {
    name,
    control,
    header,
    className,
    fullWidth,
    size,
    onChange,
    ...restSelectProps
  } = props ?? {};
  const handleOnChange = (value: string): void => {
    if (typeof onChange !== 'function') {
      return;
    }
    onChange(value);
  };
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, formState: { errors } }) => {
        const parsedError = getError(errors, name);
        const hasFormError = parsedError && !!parsedError.message;
        const { onChange } = field;
        return (
          <FormControl
            className={className}
            error={hasFormError}
            fullWidth={fullWidth}
          >
            {header && <FormLabel component="legend">{header}</FormLabel>}
            <FormGroup>
              <Select
                error={parsedError?.message}
                {...restSelectProps}
                {...field}
                onChange={(e) => {
                  onChange(e);
                  handleOnChange(e);
                }}
                displayEmpty
                disabled={restSelectProps.disabled || field.disabled}
                size={size}
              />
            </FormGroup>
          </FormControl>
        );
      }}
    />
  );
};

HookFormSelect.defaultProps = {
  header: '',
  className: '',
};

export default HookFormSelect;
